import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/constants/site";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("interests");
  const [interests, setInterests] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        const [i, e] = await Promise.all([
          axios.get(`${API}/interest?limit=200`, { withCredentials: true }),
          axios.get(`${API}/enquiry?limit=200`, { withCredentials: true }),
        ]);
        setInterests(i.data);
        setEnquiries(e.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/admin/login");
          return;
        }
        toast.error("Could not load dashboard data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const onLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#9cb4d8] font-mono text-xs uppercase tracking-widest" data-testid="admin-loading">
        Loading dashboard...
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24 bg-[#0c1427] min-h-[70vh]" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-[#38bdf8] mb-2">
              Admin Console
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              Enquiries &amp; Interests
            </h1>
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="py-2.5 px-5 rounded bg-[#203154] text-white text-xs font-bold font-mono uppercase tracking-wider hover:bg-[#38bdf8] hover:text-[#0c1427] transition-colors self-start"
          >
            Sign Out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-3 mb-8">
          <button
            type="button"
            onClick={() => setTab("interests")}
            data-testid="tab-interests"
            className={`py-2.5 px-5 rounded text-xs font-bold font-mono uppercase tracking-wider transition-colors ${
              tab === "interests"
                ? "bg-[#38bdf8] text-[#0c1427]"
                : "bg-[#162544] border border-[#203154] text-[#9cb4d8] hover:text-white"
            }`}
          >
            Course Interests ({interests.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("enquiries")}
            data-testid="tab-enquiries"
            className={`py-2.5 px-5 rounded text-xs font-bold font-mono uppercase tracking-wider transition-colors ${
              tab === "enquiries"
                ? "bg-[#38bdf8] text-[#0c1427]"
                : "bg-[#162544] border border-[#203154] text-[#9cb4d8] hover:text-white"
            }`}
          >
            Business Enquiries ({enquiries.length})
          </button>
        </div>

        {tab === "interests" && (
          <div className="rounded-xl bg-[#162544] border border-[#203154] overflow-x-auto" data-testid="interests-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-mono uppercase tracking-widest text-[#38bdf8] border-b border-[#203154]">
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Course</th>
                  <th className="px-5 py-4">Submitted</th>
                </tr>
              </thead>
              <tbody className="text-[#dce7f9]">
                {interests.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-8 text-center text-[#9cb4d8] text-xs">
                      No course interests yet.
                    </td>
                  </tr>
                )}
                {interests.map((r) => (
                  <tr key={r.id} className="border-b border-[#203154] last:border-0 hover:bg-[#0c1427]/60 transition-colors">
                    <td className="px-5 py-3.5 text-white">{r.name}</td>
                    <td className="px-5 py-3.5">{r.email}</td>
                    <td className="px-5 py-3.5 font-mono text-xs">{r.phone}</td>
                    <td className="px-5 py-3.5">{r.course}</td>
                    <td className="px-5 py-3.5 text-xs text-[#9cb4d8]">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="rounded-xl bg-[#162544] border border-[#203154] overflow-x-auto" data-testid="enquiries-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-mono uppercase tracking-widest text-[#38bdf8] border-b border-[#203154]">
                  <th className="px-5 py-4">Company</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Requirements</th>
                  <th className="px-5 py-4">Submitted</th>
                </tr>
              </thead>
              <tbody className="text-[#dce7f9]">
                {enquiries.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-[#9cb4d8] text-xs">
                      No business enquiries yet.
                    </td>
                  </tr>
                )}
                {enquiries.map((r) => (
                  <tr key={r.id} className="border-b border-[#203154] last:border-0 hover:bg-[#0c1427]/60 transition-colors">
                    <td className="px-5 py-3.5 text-white">{r.company}</td>
                    <td className="px-5 py-3.5">{r.contact_name}</td>
                    <td className="px-5 py-3.5">{r.email}</td>
                    <td className="px-5 py-3.5">{r.service}</td>
                    <td className="px-5 py-3.5 text-xs text-[#9cb4d8] max-w-xs">{r.message || "—"}</td>
                    <td className="px-5 py-3.5 text-xs text-[#9cb4d8]">{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
